// Zero-knowledge crypto layer. All operations run inside the extension.
// See docs/CRYPTO.md for the spec this implements.

import _sodium from "libsodium-wrappers-sumo";

let ready: Promise<typeof _sodium> | null = null;

export async function sodium() {
  if (ready === null) {
    ready = (async () => {
      await _sodium.ready;
      return _sodium;
    })();
  }
  return ready;
}

const MASTER_KEY_CONTEXT = new TextEncoder().encode("\x00master-key-v1");
const AUTH_HASH_CONTEXT = new TextEncoder().encode("\x00auth-hash-v1");

// libsodium MODERATE — opslimit=3, memlimit=256 MiB. Tradeoff: ~1-2s on a modern CPU,
// vs. dramatically harder offline cracking if the bcrypt(auth_hash) ever leaks.
export const KDF_DEFAULT_OPSLIMIT = 3;
export const KDF_DEFAULT_MEMLIMIT = 268435456; // 256 MiB

export async function wipe(...buffers: (Uint8Array | null | undefined)[]): Promise<void> {
  const s = await sodium();
  for (const b of buffers) {
    if (b) s.memzero(b);
  }
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

export async function deriveMasterKey(
  masterPassword: string,
  saltB64: string,
  opslimit: number,
  memlimit: number,
): Promise<Uint8Array> {
  const s = await sodium();
  const salt = s.from_base64(saltB64, s.base64_variants.ORIGINAL);
  const pwBytes = new TextEncoder().encode(masterPassword);
  return s.crypto_pwhash(
    32,
    concat(pwBytes, MASTER_KEY_CONTEXT),
    salt,
    opslimit,
    memlimit,
    s.crypto_pwhash_ALG_ARGON2ID13,
  );
}

export async function deriveAuthHash(
  masterPassword: string,
  saltB64: string,
  opslimit: number,
  memlimit: number,
): Promise<Uint8Array> {
  const s = await sodium();
  const salt = s.from_base64(saltB64, s.base64_variants.ORIGINAL);
  const pwBytes = new TextEncoder().encode(masterPassword);
  return s.crypto_pwhash(
    32,
    concat(pwBytes, AUTH_HASH_CONTEXT),
    salt,
    opslimit,
    memlimit,
    s.crypto_pwhash_ALG_ARGON2ID13,
  );
}

export async function randomBytes(n: number): Promise<Uint8Array> {
  const s = await sodium();
  return s.randombytes_buf(n);
}

// XChaCha20-Poly1305 IETF AEAD: 24-byte nonce, Poly1305 tag appended to ciphertext.
// We pass no additional_data — schema is implicit in our format.

export async function encryptBlob(
  plaintext: Uint8Array,
  key: Uint8Array,
): Promise<{ ciphertext: Uint8Array; nonce: Uint8Array }> {
  const s = await sodium();
  const nonce = s.randombytes_buf(s.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = s.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintext,
    null,
    null,
    nonce,
    key,
  );
  return { ciphertext, nonce };
}

export async function decryptBlob(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  key: Uint8Array,
): Promise<Uint8Array> {
  const s = await sodium();
  return s.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    ciphertext,
    null,
    nonce,
    key,
  );
}

export async function toB64(bytes: Uint8Array): Promise<string> {
  const s = await sodium();
  return s.to_base64(bytes, s.base64_variants.ORIGINAL);
}

export async function fromB64(b64: string): Promise<Uint8Array> {
  const s = await sodium();
  return s.from_base64(b64, s.base64_variants.ORIGINAL);
}

export async function newSalt(): Promise<Uint8Array> {
  return randomBytes(16);
}

export async function newVaultKey(): Promise<Uint8Array> {
  return randomBytes(32);
}
